import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import api from "@/axios";
import { routes } from "@/utils/urls";
import { getHasPreferredLayout } from "@/utils/localStorage";

// API Card Type (as returned from API)
export type ApiCardType =
  | "DEMOGRAPHICS"
  | "PCP"
  | "HEALTH_PLAN"
  | "CARE_MANAGEMENT"
  | "ADT_ALERTS"
  | "RISK_STRATIFICATION"
  | "CARE_GAPS"
  | "IMMUNIZATIONS"
  | "BEHAVIORAL_HEALTH_DIAGNOSES"
  | "MEDICAL_DIAGNOSES"
  | "RECENT_VISITS_ENCOUNTERS"
  | "TWELVE_MONTH_COST_UTILIZATION"
  | "MEDICATION_FILL_HISTORY"
  | "SDOH_BARRIERS"
  | "REFERRALS";

// Dashboard metadata interface
export interface DashboardMetadata {
  id: string;
  userId: string;
  layoutType: string;
  dashboardType: "default" | "preferred";
  isActive: boolean;
}

// Card interface matching API response
export interface DashboardCard {
  id: string; // Card instance ID
  dashboardId: string;
  cardId: string; // Card template ID
  layoutPosition: string | null;
  order: number;
  isFavorite: boolean;
  displayTitle: string;
  type: ApiCardType;
  accessLevels: string | null;
  desc: string;
  colspan: number;
}

// API Response interface
export interface DashboardResponse {
  status: number;
  data: {
    dashboard: DashboardMetadata;
    cards: DashboardCard[];
    hasPreferredLayout?: boolean;
  };
  message: string;
}

// Dashboard state interface
export interface DashboardState {
  dashboard: DashboardMetadata | null;
  cards: DashboardCard[];
  loading: boolean;
  error: string | null;
  hasPreferredLayout: boolean;
}

const initialState: DashboardState = {
  dashboard: null,
  cards: [],
  loading: false,
  error: null,
  hasPreferredLayout: false,
};

// Async thunk to fetch dashboard cards for the current user (derived from token).
// hasPreferredLayout is derived from the active dashboard type and localStorage
// (written when the user saves a preferred layout, cleared on logout — the same
// session boundary that previously applied to the backend preferred-layout check).
export const fetchUserDashboardCards = createAsyncThunk<
  { dashboard: DashboardMetadata; cards: DashboardCard[]; hasPreferredLayout: boolean },
  void,
  { rejectValue: string }
>(
  "dashboard/fetchUserCards",
  async (_: void, { rejectWithValue }) => {
    try {
      const response = await api.get(routes.dashboard.userDashboard());
      const data = response.data as DashboardResponse;

      if (data.status === 200 && data.data) {
        const sortedCards = [...data.data.cards].sort((a, b) => a.order - b.order);

        // hasPreferredLayout is sourced directly from the backend flag when available.
        // Falls back to local derivation (active layout type + localStorage) for
        // backward compatibility with older API versions that don't return the flag.
        const activeIsPreferred = data.data.dashboard.dashboardType === "preferred";
        const hasPreferredLayout =
          data.data.hasPreferredLayout !== undefined
            ? data.data.hasPreferredLayout
            : activeIsPreferred || getHasPreferredLayout(data.data.dashboard.userId);

        return {
          dashboard: data.data.dashboard,
          cards: sortedCards,
          hasPreferredLayout,
        };
      }

      return rejectWithValue(data.message || "Failed to fetch dashboard cards");
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to fetch dashboard cards"
      );
    }
  }
);

export const updateCardFavoriteStatus = createAsyncThunk<
  { cardInstanceId: string; isFavorite: boolean },
  {
    cardInstanceId: string;
    cardId: string;
    nextValue: boolean;
  },
  { rejectValue: string }
>(
  "dashboard/updateCardFavoriteStatus",
  async ({ cardInstanceId, cardId, nextValue }, { rejectWithValue }) => {
    try {
      await api.patch(routes.dashboard.cardFavorite(cardId), {
        favorite: nextValue,
      });

      return {
        cardInstanceId,
        isFavorite: nextValue,
      };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to update card favorite status";
      return rejectWithValue(message);
    }
  }
);

const dashboardSlice = createSlice({
  name: "dashboard",
  initialState,
  reducers: {
    // Set dashboard and cards for a specific user
    setUserDashboard: (
      state,
      action: PayloadAction<{
        dashboard: DashboardMetadata;
        cards: DashboardCard[];
      }>
    ) => {
      const { dashboard, cards } = action.payload;
      const sortedCards = [...cards].sort((a, b) => a.order - b.order);
      state.dashboard = dashboard;
      state.cards = sortedCards;
      state.error = null;
    },
    // Update a specific card for a user
    updateUserCard: (
      state,
      action: PayloadAction<{
        cardId: string; // Card instance ID
        updates: Partial<Omit<DashboardCard, "id" | "dashboardId">>;
      }>
    ) => {
      const { cardId, updates } = action.payload;
      const cardIndex = state.cards.findIndex((card) => card.id === cardId);
      if (cardIndex !== -1) {
        state.cards[cardIndex] = {
          ...state.cards[cardIndex],
          ...updates,
        };
        state.cards.sort((a, b) => a.order - b.order);
      }
    },
    // Update card order
    updateCardOrder: (
      state,
      action: PayloadAction<{
        cardId: string;
        newOrder: number;
      }>
    ) => {
      const { cardId, newOrder } = action.payload;
      const cardIndex = state.cards.findIndex((card) => card.id === cardId);
      if (cardIndex !== -1) {
        state.cards[cardIndex].order = newOrder;
        state.cards.sort((a, b) => a.order - b.order);
      }
    },
    // Toggle favorite status
    toggleCardFavorite: (
      state,
      action: PayloadAction<{ cardId: string }>
    ) => {
      const { cardId } = action.payload;
      const cardIndex = state.cards.findIndex((card) => card.id === cardId);
      if (cardIndex !== -1) {
        state.cards[cardIndex].isFavorite = !state.cards[cardIndex].isFavorite;
      }
    },
    // Clear dashboard for a specific user (full reset including hasPreferredLayout)
    clearUserDashboard: (state) => {
      state.dashboard = null;
      state.cards = [];
    },
    // Reset transient layout/UI state on logout while deliberately preserving
    // hasPreferredLayout. Because localStorage and redux-persist are both wiped
    // on logout, hasPreferredLayout CANNOT be read from storage on the next
    // login; it is re-derived from the backend by fetchUserDashboardCards.
    // Keeping it non-false here avoids a flash of "no preferred layout" in the
    // brief window between component mount and the fetch completing.
    clearDashboardState: (state) => {
      state.dashboard = null;
      state.cards = [];
      state.loading = false;
      state.error = null;
      // hasPreferredLayout is intentionally preserved — it will be authoritatively
      // re-set by fetchUserDashboardCards once the user's session is established.
    },
    // Track whether a preferred layout has been saved by the user
    setHasPreferredLayout: (state, action: PayloadAction<boolean>) => {
      state.hasPreferredLayout = action.payload;
    },
    // Set loading state
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    // Set error state
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserDashboardCards.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserDashboardCards.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        const { dashboard, cards, hasPreferredLayout } = action.payload;
        const sortedCards = [...cards].sort((a, b) => a.order - b.order);
        state.dashboard = dashboard;
        state.cards = sortedCards;
        // Authoritative value derived from the backend — no localStorage needed.
        state.hasPreferredLayout = hasPreferredLayout;
      })
      .addCase(fetchUserDashboardCards.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch dashboard cards";
      })
      .addCase(updateCardFavoriteStatus.fulfilled, (state, action) => {
        const { cardInstanceId, isFavorite } = action.payload;
        const cardIndex = state.cards.findIndex((card) => card.id === cardInstanceId);
        if (cardIndex !== -1) {
          state.cards[cardIndex].isFavorite = isFavorite;
        }
      })
      .addCase(updateCardFavoriteStatus.rejected, (state, action) => {
        state.error = action.payload || "Failed to update card favorite status";
      });
  },
});

export const {
  setUserDashboard,
  updateUserCard,
  updateCardOrder,
  toggleCardFavorite,
  clearUserDashboard,
  clearDashboardState,
  setLoading,
  setError,
  setHasPreferredLayout,
} = dashboardSlice.actions;

export default dashboardSlice.reducer;
